import { Box, Button, Tooltip, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { ComponentStyle } from '../../../interfaces/Settings/SettingsV2/ComponentStyle';
import { useThemeColor } from '../hooks/useThemeColor';
import { calculateDiscountRate } from '../../../utils/Pricing';
import { ProductSummary } from '../../../interfaces/Product/ProductSummary';
import { sharedColors } from '../../../utils/Style';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

type OverlayChipProps = {
  text: string;
  color: string;
  styleOverride?: ComponentStyle;
  stylePath: string;
};

const OverlayChip = ({
  text,
  color,
  styleOverride,
  stylePath,
}: OverlayChipProps) => {
  const { secondary } = useThemeColor();

  return (
    <EditableStyleWrapper path={stylePath}>
      <Typography
        sx={{
          backgroundColor: color,
          p: '3px 5px 2px',
          borderRadius: '3px',
          color: secondary,
          fontSize: '11px',
          fontWeight: 500,
          ...styleOverride,
        }}
      >
        {text}
      </Typography>
    </EditableStyleWrapper>
  );
};

type ProductCardProps = {
  productSummary: ProductSummary;
};

export const ProductCard = ({ productSummary }: ProductCardProps) => {
  const { primary, secondary, ternary } = useThemeColor();

  const settings = useRecoilValue(frontendSettingsAtom);

  const [hovering, setHovering] = useState(false);

  const chips: ReactNode[] = [];
  if (productSummary.specialPrice !== productSummary.price) {
    chips.push(
      <OverlayChip
        text={`%${calculateDiscountRate(
          productSummary.price,
          productSummary.specialPrice,
        ).toString()}`}
        color={primary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.discountChipStyle
        }
        stylePath='productCardSettings.discountChipStyle'
      />,
    );
  }
  if (!productSummary.inStock) {
    chips.push(
      <OverlayChip
        text='YAKINDA'
        color={ternary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.outOfStockChipStyle
        }
        stylePath='productCardSettings.outOfStockChipStyle'
      />,
    );
  } else if (productSummary.quantity < 6) {
    chips.push(
      <OverlayChip
        text='TÜKENMEK ÜZERE'
        color={ternary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.lowStockChipStyle
        }
        stylePath='productCardSettings.lowStockChipStyle'
      />,
    );
  }

  return (
    <EditableStyleWrapper path='productCardSettings.rootStyle'>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          ...settings.settingsV2?.productCardSettings?.rootStyle,
        }}
      >
        <EditableStyleWrapper path='productCardSettings.imageStyle'>
          <Box
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            sx={{
              position: 'relative',
              width: '100%',
              paddingBottom: '120%', // 1.2 * width = 120% height
              overflow: 'hidden',
            }}
          >
            <Box
              component='img'
              src={
                hovering && !!productSummary.secondImage
                  ? productSummary.secondImage
                  : productSummary.imagePath
              }
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                objectFit: 'contain',
                ...settings.settingsV2?.productCardSettings?.imageStyle,
              }}
            />

            {chips.map((chip, i) => (
              <Box
                key={i}
                position='absolute'
                top={5 + Math.floor(i / 2) * 30}
                left={i % 2 === 0 ? 5 : undefined}
                right={i % 2 === 0 ? undefined : 5}
                zIndex={1}
              >
                {chip}
              </Box>
            ))}
          </Box>
        </EditableStyleWrapper>

        <Tooltip title={productSummary.name}>
          <EditableStyleWrapper path='productCardSettings.nameStyle'>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                textAlign: 'center',
                color: sharedColors.gray7,
                transition: 'color 0.3s ease',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                lineHeight: '1.3em',
                maxHeight: '2.6em',
                minHeight: '2.6em',
                '&:hover': {
                  color: primary,
                },
                ...settings.settingsV2?.productCardSettings?.nameStyle,
              }}
            >
              {productSummary.name}
            </Typography>
          </EditableStyleWrapper>
        </Tooltip>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {productSummary.specialPrice !== productSummary.price && (
            <EditableStyleWrapper path='productCardSettings.oldPriceStyle'>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '15px',
                  color: sharedColors.gray5,
                  textDecoration: 'line-through',
                  ...settings.settingsV2?.productCardSettings?.oldPriceStyle,
                }}
              >
                {productSummary.priceFormatted}
              </Typography>
            </EditableStyleWrapper>
          )}

          <EditableStyleWrapper path='productCardSettings.priceStyle'>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: primary,
                ...settings.settingsV2?.productCardSettings?.priceStyle,
              }}
            >
              {productSummary.specialPrice
                ? productSummary.specialPriceFormatted
                : productSummary.priceFormatted}
            </Typography>
          </EditableStyleWrapper>
        </Box>

        <EditableStyleWrapper
          path={
            productSummary.inStock
              ? 'productCardSettings.addToCartButtonStyle'
              : 'productCardSettings.notifyButtonStyle'
          }
        >
          <Button
            sx={{
              fontWeight: 700,
              fontSize: '13px',
              padding: '8px 15px',
              color: secondary,
              backgroundColor: productSummary.inStock ? primary : ternary,
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: ternary,
              },
              ...(productSummary.inStock
                ? settings.settingsV2?.productCardSettings?.addToCartButtonStyle
                : settings.settingsV2?.productCardSettings?.notifyButtonStyle),
            }}
          >
            {productSummary.inStock ? 'Sepete Ekle' : 'Haber Ver'}
          </Button>
        </EditableStyleWrapper>
      </Box>
    </EditableStyleWrapper>
  );
};
